import {
  getMatchingBundleOffers,
  getBundlePriceSummary
} from '@/lib/bundles/utils';

/**
 * Filters a product's variants down to just the ones matching its secondary
 * attribute (e.g. firmness).
 */
export function withFilteredVariants(productNode, secondaryAttribute) {
  const variants = productNode?.shopifyProduct?.variants.filter(
    (variant) =>
      !secondaryAttribute || variant.title.includes(secondaryAttribute)
  );

  return {
    ...productNode,
    shopifyProduct: { ...productNode?.shopifyProduct, variants }
  };
}

/**
 * Resolves which bundle product should be selected from the URL's
 * `?selectedProduct=` handle, falling back to the block's configured default
 * product when there's no match (including when there's no param at all).
 *
 * `searchParams` is the plain object Next.js resolves the page's `searchParams`
 * promise to (e.g. { selectedProduct: 'some-handle' }).
 */
export function getBundleProductFromSearchParams(
  selectorProducts = [],
  searchParams
) {
  const handleParam = searchParams?.selectedProduct;
  const handle = Array.isArray(handleParam) ? handleParam[0] : handleParam;

  const matchedBlock =
    selectorProducts.find(
      (block) => block?.product?.[0]?.shopifyProduct.handle === handle
    ) || selectorProducts.find((block) => block.default_product);

  if (!matchedBlock?.product?.[0]) return null;

  return withFilteredVariants(
    matchedBlock.product[0],
    matchedBlock.default_secondary_attribute
  );
}

/**
 * Packages the current bundle offers into a nicer format than the raw
 * bundleData from Contentstack, in which you'd have to cross reference a
 * metafield to get the discount amount, among other things.
 *
 * Returns null for non-bundle pages (and for bundles missing required data),
 * which is how consumers tell there's no bundle to render.
 */
export function buildBundleState({
  isBundlePage,
  bundleData,
  shopifyProduct,
  options,
  selectedProduct,
  setSelectedProduct
}) {
  if (!isBundlePage) return null;

  if (!bundleData?.bundlePricingMetafield) {
    console.warn('Missing bundlePricingMetafield in bundleData');
    return null;
  }

  if (!bundleData.bundleProducts?.length) {
    console.warn('Missing bundleProducts in bundleData');
    return null;
  }

  // The bundle tracks the primary product's first option (usually size)
  const firstOptionName = shopifyProduct?.options?.[0]?.name.toLowerCase();
  const selectedOptionValue = firstOptionName
    ? options[firstOptionName]
    : undefined;

  const currentOffers = getMatchingBundleOffers(
    bundleData.bundlePricingMetafield,
    bundleData.bundleProducts,
    selectedOptionValue,
    selectedProduct
  );

  const priceSummary = getBundlePriceSummary(
    bundleData.bundlePricingMetafield,
    currentOffers,
    selectedOptionValue
  );

  const bundleName = bundleData.bundlePricingMetafield.name;

  return {
    // Contains the selected bundle variants and other required metadata
    currentOffers,

    // The currently selected option, read from the primary product
    selectedOption: { name: firstOptionName, value: selectedOptionValue },

    // Contains the overall price summary of the bundle
    priceSummary,

    // A dynamically changing identifier based on what's selected.
    // Helps with changing bundle quantities properly in the cart.
    bundleId: `${bundleName.replaceAll(' ', '-')}-${selectedOptionValue}`,

    // The name of the bundle, unchanging, found in the bundle metafield
    bundleName,

    // The stock of the bundle, set on contentstack
    stock: bundleData.stock || 0,

    // Contains everything else in bundleData EXCEPT bundleProducts
    // (redundant thanks to the more helpful "currentOffers" field.)
    details: {
      pricingMetafield: bundleData.bundlePricingMetafield,
      gwpOptionManager: bundleData.gwpOptionManager,
      url: bundleData.url,
      bundleSettings: bundleData.bundleSettings
    },

    // The selected product in the state
    selectedProduct,

    // Updates the selected product and reflects it in the URL
    setSelectedProduct
  };
}
